import {
  WSButton,
  WSFlexBox,
  WSLoader,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { RecurrenceScheduleDateStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { queriesPayerRows, queriesRecurringInvoices } from "@query";
import { getLocalStartDayByUTC, getStartOfDay } from "../../utils/dates";
import { selectorRecurrenceCadenceLabel } from "../../shared/selectors/selectorRecurrenceCadenceLabel";
import { DrawerLayout } from "../components/DrawerLayout/DrawerLayout";
import {
  InvoiceSeriesTable,
  SeriesTableItemData
} from "../components/InvoiceSeriesTable/InvoiceSeriesTable";
import { SeriesInvoiceActionsModal } from "../components/SeriesInvoiceActionsModal/SeriesInvoiceActionsModal";
import { getRecurringInvoiceStatusPillProps } from "../utils/getRecurringInvoiceStatusPillProps";

export const InvoicesSeriesDetails: React.FC<
  RouteComponentProps<{
    invoiceTemplateId: string;
  }>
> = ({ match, location }) => {
  const history = useHistory();

  const recurringInvoiceId = match.params.invoiceTemplateId;
  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId
    });
  const payerId = queryRecurringInvoice.data?.payerId;
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    { filter: { id: { eq: payerId! } }, page: { size: 1 } },
    { enabled: !!payerId, retry: false }
  );

  const recurringInvoice = queryRecurringInvoice.data;
  const payerRow = queryPayerRow.data?.data?.[0];

  const onBack = () => {
    history.replace({
      pathname: location.pathname.replace("/series", ""),
      search: location.search
    });
  };

  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);

  const [upcomingData, pastData] = useMemo(() => {
    const result: [SeriesTableItemData[], SeriesTableItemData[]] = [[], []];

    (queryRecurringInvoice.data?.schedule?.scheduleDates || []).forEach(
      (scheduleDate, index) => {
        // An occurrence that has already generated its invoice is history even
        // when it was generated today, so status decides before the date does
        // (PLA-3590).
        const isPast =
          scheduleDate.status === RecurrenceScheduleDateStatus.COMPLETED ||
          getLocalStartDayByUTC(scheduleDate.date) < getStartOfDay();

        result[isPast ? 1 : 0].push({ index, scheduleDate });
      }
    );

    return result;
  }, [queryRecurringInvoice.data?.schedule?.scheduleDates]);

  const initialUpcomingLimit = pastData.length > 0 ? 3 : 6;

  if (!recurringInvoice) {
    return (
      <DrawerLayout title="Series details" onBack={onBack}>
        <WSLoader.Spinner />
      </DrawerLayout>
    );
  }

  return (
    <DrawerLayout title="Series details" onBack={onBack}>
      <SeriesInvoiceActionsModal />

      <WSFlexBox.CenterY mb="XL" justify="space-between" wrap="nowrap">
        <WSText>
          {selectorRecurrenceCadenceLabel(recurringInvoice.schedule)}
        </WSText>
        <WSPill {...getRecurringInvoiceStatusPillProps(recurringInvoice)} />
      </WSFlexBox.CenterY>

      <WSText mb="XL">Select invoice to edit</WSText>

      {upcomingData.length > 0 && (
        <>
          <WSText.ParagraphSm color="gray500" mb="XS">
            Upcoming invoices
          </WSText.ParagraphSm>
          <InvoiceSeriesTable
            data={upcomingData.slice(
              0,
              showAllUpcoming ? undefined : initialUpcomingLimit
            )}
            parentRecurringInvoice={recurringInvoice}
            payerRow={payerRow}
            mb="XS"
          />
          {!showAllUpcoming &&
            upcomingData.length - initialUpcomingLimit > 0 && (
              <WSButton.Link
                onClick={() => {
                  setShowAllUpcoming(true);
                }}
              >
                Load {upcomingData.length - initialUpcomingLimit} more
              </WSButton.Link>
            )}
        </>
      )}

      {pastData.length > 0 && (
        <>
          <WSText.ParagraphSm color="gray500" mt="2XL" mb="XS">
            Past invoices
          </WSText.ParagraphSm>
          <InvoiceSeriesTable
            noActions
            data={pastData.slice(0, showAllPast ? undefined : 3)}
            parentRecurringInvoice={recurringInvoice}
            payerRow={payerRow}
            mb="XS"
          />
          {!showAllPast && pastData.length - 3 > 0 && (
            <WSButton.Link
              onClick={() => {
                setShowAllPast(true);
              }}
            >
              Load {pastData.length - 3} more
            </WSButton.Link>
          )}
        </>
      )}
    </DrawerLayout>
  );
};
